<script lang="ts" setup>
import { t } from '@/i18n/utils'

defineProps<{
  config: any
  skeleton: boolean
  isEmailLogin: number
  availableLogins: string[]
  showLogout?: string
  form: any
  userAgreementUrl: string
  privacyPolicyUrl: string
  loginBgUrl: string
  signInWithFacebook: () => void
  signInWithGoogle: () => void
  signInWithApple: () => void
  logout: () => void
  setEmailLogin: () => void
  getCode: () => void
  beforeSignIn: () => void
  back: () => void
}>()

const emit = defineEmits<{
  (e: 'update-form-field', field: 'email' | 'code', value: string): void
}>()

function updateFormField(field: 'email' | 'code', event: Event) {
  emit('update-form-field', field, (event.target as HTMLInputElement).value)
}
</script>

<template>
  <div class="login-page">
    <div v-if="!skeleton" class="login-bg">
      <div class="novelyn-login-back fixed top-32px left-12px" @click="back()">
        <i-icon-park-outline-left class="w-4 h-4" />
      </div>

      <div v-if="!isEmailLogin" class="novelyn-login-options mt-180px flex flex-col items-center font-medium">
        <img :src="config.resources.logo" width="80">
        <img :src="config.resources.novel" width="108" class="mt-4">
        <p class="novelyn-tagline">
          {{ t('NovelynTagline') }}
        </p>
        <div class="novelyn-login-card">
          <div
            v-if="availableLogins.includes('facebook')"
            class="mt-4 relative w-300px h-45px border-rd-25px text-n-text-inverse flex items-center justify-center facebook-btn login-third-btn"
            @click="signInWithFacebook"
          >
            <img :src="config.resources.facebookIcon" width="20" class="absolute top-50% mt--10px left-30px">
            <span class="login-third-btn__text">{{ config.texts.signInWithFacebook }}</span>
          </div>
          <div
            v-if="availableLogins.includes('google')"
            class="mt-4 relative w-300px h-45px border-rd-25px text-n-text-inverse flex items-center justify-center google-btn login-third-btn"
            @click="signInWithGoogle"
          >
            <img :src="config.resources.googleIcon" width="20" class="absolute top-50% mt--10px left-30px">
            <span class="login-third-btn__text">{{ config.texts.signInWithGoogle }}</span>
          </div>
          <div
            v-if="availableLogins.includes('apple')"
            class="mt-4 relative w-300px h-45px border-rd-25px text-n-text-inverse flex items-center justify-center apple-btn login-third-btn"
            @click="signInWithApple"
          >
            <img :src="config.resources.appleIcon" width="20" class="absolute top-50% mt--10px left-30px">
            <span class="login-third-btn__text">{{ config.texts.signInWithApple }}</span>
          </div>

          <div v-if="showLogout === '1'" class="mt-4 relative w-300px h-45px border-rd-25px text-n-text-inverse flex items-center justify-center google-btn" @click="logout">
            {{ config.texts.logout }}
          </div>
          <div class="novelyn-divider">
            {{ t('Or') }}
          </div>
          <div class="novelyn-email-link text-center" @click="setEmailLogin">
            {{ config.texts.continueWithEmail }}
          </div>
        </div>
      </div>

      <div v-else class="novelyn-email-card">
        <div class="novelyn-email-title">
          {{ config.texts.signInWithEmail }}
        </div>
        <div class="novelyn-email-subtitle">
          {{ t('VerificationCodeInbox') }}
        </div>

        <input
          :value="form.email"
          class="novelyn-email-input"
          :placeholder="config.texts.yourEmail"
          maxlength="50"
          @input="updateFormField('email', $event)"
        >

        <div class="novelyn-code-row">
          <input
            :value="form.code"
            class="novelyn-email-input novelyn-code-input"
            :placeholder="config.texts.verificationCode"
            maxlength="6"
            @input="updateFormField('code', $event)"
          >
          <button
            type="button"
            class="novelyn-code-action"
            :class="{ 'novelyn-code-action--countdown': form.timeout > 0 }"
            :disabled="form.timeout > 0"
            @click="getCode"
          >
            {{ form.timeout > 0 ? `${form.timeout}s` : config.texts.getVerificationCode }}
          </button>
        </div>

        <button type="button" class="novelyn-sign-in" @click="beforeSignIn">
          {{ config.texts.signIn }}
        </button>
      </div>

      <div class="novelyn-login-agreement">
        {{ config.texts.userAgreement }} <a :href="userAgreementUrl">{{ config.texts.userAgreementLink }}</a> {{ config.texts.and }} <a :href="privacyPolicyUrl">{{ config.texts.privacyPolicyLink }}</a>
      </div>
    </div>

    <div v-else class="h-100dvh">
      <div class="pt-20vh flex justify-center">
        <BasicSpinner class="mx-auto" />
      </div>
      <p class="mt-4 text-center font-bold">
        {{ config.texts.loading }}
      </p>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.login-bg {
  width: 100vw;
  height: 100vh;
  min-height: 660px;
  overflow: hidden;
  background: var(--novelyn-gradient);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  position: relative;

  &::before,
  &::after {
    content: '';
    position: absolute;
    border-radius: 50%;
    background: rgba(255, 255, 255, .12);
  }

  &::before {
    top: -90px;
    right: -90px;
    width: 280px;
    height: 280px;
  }

  &::after {
    bottom: 60px;
    left: -80px;
    width: 200px;
    height: 200px;
  }

  a {
    text-decoration: underline;
  }
}

.novelyn-login-options {
  position: relative;
  z-index: 2;
  width: 100%;
  margin-top: 76px;

  > img:first-child {
    padding: 14px;
    border-radius: 24px;
    background: rgba(255, 255, 255, .22);
  }
}

.novelyn-login-card,
.novelyn-email-card {
  width: calc(100% - 40px);
  margin-top: 36px;
  padding: 20px;
  border-radius: 26px;
  background: rgba(255, 255, 255, .97);
  box-shadow: 0 20px 44px rgba(140, 40, 75, .30);
}

.novelyn-login-card {
  display: flex;
  flex-direction: column;

  .apple-btn { order: 1; }
  .google-btn { order: 2; }
  .facebook-btn { order: 3; }
  .novelyn-divider { order: 4; }
  .novelyn-email-link { order: 5; }
}

.novelyn-login-back {
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  color: #fff;
  background: rgba(255, 255, 255, .25);
  border-radius: 50%;
}

.novelyn-tagline {
  margin-top: 6px;
  color: rgba(255, 255, 255, .85);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 3px;
}

.novelyn-divider {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 16px 0;
  color: var(--novelyn-ink-2);
  font-size: 11px;
  font-weight: 700;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--novelyn-line);
  }
}

.novelyn-email-link {
  color: var(--novelyn-rose);
  font-size: 13px;
  font-weight: 900;
}

.novelyn-email-card {
  position: relative;
  z-index: 2;
  margin-top: 140px;
  padding: 24px 20px;
  box-shadow: 0 20px 44px rgba(140, 40, 75, .35);

  .novelyn-email-title {
    margin-bottom: 4px;
    color: var(--novelyn-ink);
    font-family: 'Playfair Display', Georgia, serif;
    font-size: 20px;
    font-weight: 700;
  }

  .novelyn-email-subtitle {
    margin-bottom: 18px;
    color: var(--novelyn-ink-2);
    font-size: 11px;
    font-weight: 700;
  }
}

.novelyn-email-input {
  width: 100%;
  height: auto;
  padding: 13px 16px;
  color: var(--novelyn-ink);
  font-family: inherit;
  font-size: 14px;
  font-weight: 700;
  line-height: 21px;
  background: var(--novelyn-rose-faint);
  border: 1px solid var(--novelyn-line);
  border-radius: 16px;
  outline: none;

  &::placeholder {
    color: var(--novelyn-ink-3);
    opacity: 1;
  }

  &:focus {
    border-color: var(--novelyn-rose);
  }
}

.novelyn-code-row {
  display: flex;
  gap: 10px;
  margin: 12px 0 18px;
}

.novelyn-code-input {
  flex: 1;
  min-width: 0;
}

.novelyn-code-action {
  display: flex;
  align-items: center;
  flex: none;
  padding: 0 16px;
  color: var(--novelyn-rose-deep);
  font-family: inherit;
  font-size: 12px;
  font-weight: 900;
  white-space: nowrap;
  background: var(--novelyn-rose-faint);
  border: 1px solid var(--novelyn-rose);
  border-radius: 16px;

  &--countdown {
    color: var(--novelyn-ink-2);
    border-color: var(--novelyn-line);
  }
}

.novelyn-sign-in {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 13px 26px;
  color: #fff;
  font-family: inherit;
  font-size: 14px;
  font-weight: 800;
  line-height: 21px;
  background: var(--novelyn-gradient);
  border: 0;
  border-radius: 999px;
  box-shadow: 0 8px 18px rgba(224, 90, 126, .35);
}

.novelyn-login-agreement {
  position: absolute;
  z-index: 2;
  right: 30px;
  bottom: 34px;
  left: 30px;
  color: rgba(255, 255, 255, .85);
  font-size: 10px;
  font-weight: 600;
  text-align: center;
}

.login-third-btn {
  padding-left: 56px;
  padding-right: 16px;
  box-sizing: border-box;
}

.login-third-btn__text {
  display: block;
  width: 100%;
  min-width: 0;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.facebook-btn {
  color: var(--novelyn-ink) !important;
  border: 1px solid var(--novelyn-line);
  background: #fff;
}

.google-btn {
  color: var(--novelyn-ink) !important;
  border: 1px solid var(--novelyn-line);
  background: #fff;
}

.apple-btn {
  color: #fff !important;
  background: var(--novelyn-ink);
}
</style>
